// Everything the signed-out landing page says, in English and Swedish
// (components/Landing.tsx renders it; scripts/prerender.mjs writes it into
// static HTML for crawlers and agents, with the FAQ as structured data). One
// copy per language, the same shape for both — landingCopy.test.ts checks.
//
// Say only what the product does today, and hide nothing about what it costs:
// the pricing section and the FAQ spell out the trial, what Free keeps, what
// Plus adds, and every term of the founding offer. The comparison names other
// apps only for things their own listings say (as of COMPARED_AS_OF). The
// pricing cards take their amounts from Stripe at runtime (lib/pricing.ts);
// the FAQ, which is also structured data, quotes FALLBACK_PRICES — the hand
// copy pricing.test.ts pins to the amounts docs/OPERATIONS.md sets in Stripe.

import {
  FALLBACK_PRICES, FREE_FEATURES, FREE_ITEM_LIMIT, LAUNCH_OFFER, MAX_MEMBERS, PLAN_NAME, PLUS_FEATURES, TRIAL_DAYS,
  type PlanId,
} from './pricing'
import { formatMoney } from './currency'

export type LandingLang = 'en' | 'sv'

export const LANDING_LANGS: readonly LandingLang[] = ['en', 'sv']

/** The public path of each language's landing page. */
export const LANDING_PATH: Record<LandingLang, string> = { en: '/', sv: '/sv' }

/** The comparison's date: other apps change, so the table says when it was true. */
export const COMPARED_AS_OF = '2026-10'

/** A bold lead and the rest of the sentence. */
export interface Point { lead: string; text: string }

/** A comparison cell: yes, no, or a few words. */
export type Cell = boolean | string

// A local const, so its null check carries into the copy's callbacks.
const OFFER = LAUNCH_OFFER

/** The comparison's month, in the page's language: "October 2026", "oktober 2026". */
function comparedOn(lang: LandingLang): string {
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${COMPARED_AS_OF}-01T00:00:00Z`))
}

/** Each currency written the way its own country writes it: 149 kr, $14.99, £12.99, €14.99. */
const NATIVE = { sek: 'sv-SE', usd: 'en-US', gbp: 'en-GB', eur: 'en-IE' } as const

/** Plus in all four currencies, as the FAQ quotes it: "149 kr, $14.99, £12.99 or €14.99". */
function inEveryCurrency(plan: PlanId, lang: LandingLang): string {
  const amounts = (['sek', 'usd', 'gbp', 'eur'] as const)
    .map(c => formatMoney(FALLBACK_PRICES.currencies[c][plan], c, NATIVE[c]))
  return `${amounts.slice(0, -1).join(', ')} ${lang === 'sv' ? 'eller' : 'or'} ${amounts[amounts.length - 1]}`
}

export interface LandingCopy {
  meta: { title: string; description: string; ogTitle: string; ogDescription: string; ogImageAlt: string; locale: string }
  nav: { how: string; recipes: string; garmin: string; pricing: string; faq: string; signIn: string; open: string; otherLang: string; sections: string }
  hero: { eyebrow: string; title: string[]; lead: string; garminCta: string; worksOn: string; platforms: string[] }
  film: { eyebrow: string; title: string[]; alt: string }
  /** Chapter 03, shopping together (the field name predates the chapters; seo.ts reads its points). */
  shop: { eyebrow: string; title: string[]; body: string; points: Point[] }
  /** Chapter 02: the list in the store's walking order. `route` is the card's example list, in walking order. */
  store: { eyebrow: string; title: string[]; body: string; points: Point[]; routeLabel: string; route: [string, string][] }
  plan: { eyebrow: string; title: string[]; body: string; shotAlt: string }
  recipes: { eyebrow: string; title: string[]; body: string; points: Point[]; links: string[] }
  usual: { eyebrow: string; title: string[]; body: string; chipsLabel: string; chips: [string, string, string][] }
  garmin: { eyebrow: string; title: string[]; body: string; points: string[]; stat: { value: string; label: string } }
  /** After the chapters: planning (plan), Running low (usual) and the trip summary, side by side. */
  more: { eyebrow: string; title: string[]; summary: { title: string; body: string; stats: [string, string][] } }
  setup: { title: string[]; steps: { title: string; text: string }[] }
  compare: {
    eyebrow: string; title: string[]; body: string; columns: string[]
    rows: { label: string; cells: Cell[] }[]
    note: string; yes: string; no: string
  }
  pricing: {
    eyebrow: string; title: string
    /** How it works, in order: the trial, Free, Plus. */
    howTitle: string; how: string[]
    free: { name: string; price: string; per: string; features: readonly string[] }
    plus: {
      name: string; perYear: string
      orMonthly: (price: string) => string; bestValue: (perMonth: string) => string
      features: readonly string[]
    }
    cta: string; small: string
    /** The founding offer, every term; null once it has ended (LAUNCH_OFFER). */
    founder: { title: string; points: (first: string, then: string) => string[] } | null
    tax: (included: boolean) => string; conversion: string; pricesIn: string; change: string; currency: string
  }
  faq: { title: string; items: { q: string; a: string }[] }
  cta: { title: string[] }
  form: { eyebrow: string; title: string; region: string }
  /** Before launch (the waitlist): what replaces signing up. Pricing is shown just the same. */
  waitlist: { cta: string; eyebrow: string; title: string; body: string; region: string; cost: string; notMember: string; member: string }
  footer: { privacy: string; terms: string }
}

const en: LandingCopy = {
  meta: {
    title: 'Matlist: shared grocery list for your household and your Garmin',
    description: 'One grocery list for the whole household. Import recipes from any site, YouTube, TikTok or Instagram, plan the week, and shop in your store’s order on your phone or Garmin watch.',
    ogTitle: 'Matlist: plan at home, shop on your wrist',
    ogDescription: 'One grocery list for the whole household, on every phone and your Garmin watch.',
    ogImageAlt: 'Matlist: plan at home, shop on your wrist. The shared list on a phone and a Garmin watch.',
    locale: 'en_US',
  },
  nav: { how: 'How it works', recipes: 'Recipes', garmin: 'Garmin', pricing: 'Pricing', faq: 'FAQ', signIn: 'Sign in', open: 'Open Matlist', otherLang: 'Svenska', sections: 'Sections' },
  hero: {
    eyebrow: 'Shared grocery list',
    title: ['Plan at home.', 'Shop on', 'your wrist.'],
    lead: 'Matlist is one grocery list for the whole household. Plan the week from your recipes, then shop in your store’s order on your phone or your Garmin watch.',
    garminCta: 'Get the Garmin app',
    worksOn: 'Works on',
    platforms: ['Web', 'iPhone', 'Android', 'iPad and desktop', 'Garmin watches'],
  },
  film: {
    eyebrow: 'In 47 seconds',
    title: ['Wrist. Store.', 'Together. Recipes.'],
    alt: 'A short film in four parts. Groceries ticked off on a Garmin watch while the phone stays in a pocket; Matlist runs on more than 70 Garmin watches. A list that sorts itself into the order you walk your store and learns from every trip. Joining someone who is already shopping, so a tick on the phone or the watch is gone on both. A recipe from a TikTok, Instagram, YouTube or web link going onto the list, asking first about the eggs already on it. The phone and watch screens are the real app.',
  },
  shop: {
    eyebrow: 'Together',
    title: ['Tick on either.', 'Gone on both.'],
    body: 'Everyone adds to the same list. In the shop, two of you are one trip with one basket: whatever one of you ticks is gone from every phone and watch within a second, so nobody buys the milk twice.',
    points: [
      { lead: 'Join', text: 'when someone is already shopping: their name is on the button, on your phone and on your watch.' },
      { lead: 'One basket:', text: 'swipe right on the phone or press Start on the watch, and it’s in. Undo is one tap away.' },
      { lead: 'Finish trip', text: 'ends it for everyone and shows each of you the summary.' },
    ],
  },
  store: {
    eyebrow: 'Your store',
    title: ['Your list', 'walks your', 'store.'],
    body: 'Pick the store when you start shopping and the list puts itself in the order you walk it. Every trip teaches it a little more, so the next one is quicker.',
    points: [
      { lead: 'Start shopping', text: 'and pick the store. Somewhere new? It sorts by aisle until it knows.' },
      { lead: 'Learns from every trip:', text: 'the order you put things in the basket is the order it shows next time.' },
      { lead: 'Opens without signal,', text: 'and catches up as soon as you’re back online.' },
    ],
    routeLabel: 'In walking order',
    route: [['Sourdough bread', 'bakery'], ['Bananas', 'produce'], ['Apples', 'produce'], ['Pasta', 'pantry'], ['Peas', 'frozen'], ['Milk', 'dairy'], ['Eggs', 'dairy']],
  },
  plan: {
    eyebrow: 'At home',
    title: ['Plan at home.'],
    body: 'On a laptop or an iPad, Matlist becomes a planning board. Every category is open and has its own add field, so the weekly shop takes a couple of minutes.',
    shotAlt: 'The planning board on a laptop: every category open, each with its own add field.',
  },
  recipes: {
    eyebrow: 'Recipes',
    title: ['Any recipe.', 'Straight to', 'the list.'],
    body: 'Paste a link from a recipe site, YouTube, TikTok or Instagram. Matlist reads the recipe and names every ingredient the way your household already does, so nothing ends up on the list twice.',
    points: [
      { lead: 'Import any link:', text: 'recipe sites, YouTube, TikTok and Instagram. The photo and the page’s rating come along.' },
      { lead: 'Add to list', text: 'merges what’s already there and asks before it doubles anything.' },
      { lead: 'Plan the week', text: 'day by day, and cook with a timer for every step.' },
    ],
    links: ['tiktok.com/@…/video/…', 'instagram.com/reel/…', 'youtube.com/watch?v=…', 'any recipe page'],
  },
  usual: {
    eyebrow: 'Running low',
    title: ['It remembers', 'the usual.'],
    body: 'Matlist notices how often you buy things. When the oat milk is due again, it shows up as a suggestion. One tap puts it back on the list.',
    chipsLabel: 'Running low',
    chips: [['Oat milk', 'Every 5 days', 'dairy'], ['Eggs', '8 days ago', 'dairy'], ['Bananas', 'Weekly', 'produce'], ['Coffee beans', 'Every 2 weeks', 'pantry']],
  },
  garmin: {
    eyebrow: 'Garmin',
    title: ['Tick it off.', 'Phone stays', 'in your pocket.'],
    body: 'Start shopping on the watch and the list rolls along the round of the screen in your store’s order. Press Start and the item is in the basket, with Undo right by the Back button.',
    points: [
      'Join someone’s trip from your wrist',
      'Every button works, no touch needed',
      'Pairs in seconds: scan the watch’s code, type the code from the app',
    ],
    stat: { value: '70+', label: 'Garmin watches: Forerunner, fēnix, Venu and more' },
  },
  more: {
    eyebrow: 'And more',
    title: ['Everything around', 'the trip.'],
    summary: {
      title: 'The trip, in numbers',
      body: 'Finish a trip and everyone gets the summary: time in the store, aisle by aisle, who added what, and what it cost.',
      stats: [['14:32', 'In the store'], ['12', 'Items'], ['5', 'Aisles']],
    },
  },
  setup: {
    title: ['Set up in a minute.'],
    steps: [
      { title: 'Create a household', text: 'Open Matlist, name your household and you get a short code.' },
      { title: 'Share the code', text: 'Everyone enters it on their phone. Your watch pairs with a code from Settings.' },
      { title: 'Shop together', text: 'Add from anywhere. Whoever is in the shop ticks things off.' },
    ],
  },
  compare: {
    eyebrow: 'Compared',
    title: ['How Matlist', 'compares.'],
    body: 'Most list apps share a list. Matlist is built around the whole trip: planning, the shop and your wrist.',
    columns: ['Matlist', 'Bring!', 'AnyList', 'Google Keep'],
    rows: [
      { label: 'One list the household edits live', cells: [true, true, true, true] },
      { label: 'Garmin watch app', cells: [true, false, false, false] },
      { label: 'Other watches', cells: [false, 'Apple Watch, Wear OS', 'Apple Watch', 'Wear OS'] },
      { label: 'Recipes from web links', cells: [true, true, true, false] },
      { label: 'Recipes from YouTube, TikTok and Instagram', cells: [true, '', '', false] },
      { label: 'Store order learned from your trips', cells: [true, '', '', false] },
      { label: 'Runs in the browser, nothing to install', cells: [true, '', '', true] },
      { label: 'Free plan with a shared list', cells: [`Yes, up to ${FREE_ITEM_LIMIT} items`, true, true, true] },
      { label: 'Paid plan, per year (US price)', cells: ['$14.99, whole household', '$8.99 (Premium)', '$14.99 household, $9.99 one person', 'No paid plan'] },
    ],
    note: `Other apps as described in their own app store listings, pricing and help pages, ${comparedOn('en')}. Prices are as each app lists them in the US, before tax. A blank cell means we couldn’t tell for sure.`,
    yes: 'Yes',
    no: 'No',
  },
  pricing: {
    eyebrow: 'Pricing', title: `${TRIAL_DAYS} days of everything. Then Free, or ${PLAN_NAME} for the whole household.`,
    howTitle: 'How it works',
    how: [
      `Start with ${TRIAL_DAYS} days of everything, the watch app included. No card, nothing to cancel.`,
      `When the trial ends, your household moves to Free on its own: the shared list keeps working with up to ${FREE_ITEM_LIMIT} items at a time. Nothing is deleted and nothing is charged.`,
      `Get ${PLAN_NAME} whenever you like, yearly or monthly, for everyone in the household. Cancel any time: ${PLAN_NAME} runs to the end of what you paid for, then you’re back on Free.`,
    ],
    free: { name: 'Free', price: 'Free', per: 'for as long as you like', features: FREE_FEATURES },
    plus: {
      name: PLAN_NAME, perYear: 'a year',
      orMonthly: p => `or ${p} a month`, bestValue: p => `Best value — under ${p} a month`,
      features: ['Everything in Free', ...PLUS_FEATURES],
    },
    cta: `Start your ${TRIAL_DAYS}-day free trial`,
    small: `Billed per household, not per person: one ${PLAN_NAME} covers up to ${MAX_MEMBERS} people.`,
    founder: OFFER && {
      title: `Founding offer: ${OFFER.percentOff}% off your first year`,
      points: (first, then) => [
        `${PLAN_NAME} yearly for ${first} the first year, then ${then} a year.`,
        `For the first ${OFFER.households} households, for ${OFFER.days} days after launch.`,
        'Applied automatically when you choose yearly. No code to type.',
        'For new customers, one per household. The monthly plan isn’t discounted.',
        'It renews at the full price. Cancel any time before then.',
      ],
    },
    tax: included => (included ? 'VAT included. Cancel any time.' : 'Plus VAT where it applies. Cancel any time.'),
    conversion: 'Checkout may show this in your local currency.', pricesIn: 'Prices in', change: 'Change', currency: 'Currency',
  },
  faq: {
    title: 'Questions',
    items: [
      { q: 'What does it cost?', a: `Matlist starts with ${TRIAL_DAYS} days of everything, free and without a card. After that your household is on Free, the shared list with up to ${FREE_ITEM_LIMIT} items at a time, for as long as you like. ${PLAN_NAME} is everything, for up to ${MAX_MEMBERS} people: ${inEveryCurrency('annual', 'en')} a year, or ${inEveryCurrency('monthly', 'en')} a month, plus VAT where it applies.` },
      { q: 'What stays free?', a: `The shared list, live on everyone’s phone, with up to ${FREE_ITEM_LIMIT} items at a time; ticking an item off frees its place. Categories, shopping mode with your basket, the trip summary, your saved recipes to read and cook from, and up to ${MAX_MEMBERS} people. The watch shows your list; ticking off from your wrist is ${PLAN_NAME}.` },
      { q: 'What happens when the trial ends?', a: `Nothing you need to do. Your household moves to Free on its own, keeps all its data, and nothing is charged. If the list holds more than ${FREE_ITEM_LIMIT} items, everything stays; you can add again once it’s under ${FREE_ITEM_LIMIT}. Get ${PLAN_NAME} at any time to have everything back.` },
      ...(OFFER ? [{ q: 'How does the founding offer work?', a: `The first ${OFFER.households} households that choose ${PLAN_NAME} yearly within ${OFFER.days} days of launch pay ${OFFER.percentOff}% less for their first year. It’s applied automatically at checkout (it’s the code ${OFFER.code}), so there’s nothing to type. It’s for new customers, one per household, and not for the monthly plan. After the first year ${PLAN_NAME} renews at the full price, and you can cancel any time.` }] : []),
      { q: 'Can I cancel?', a: `Yes, any time, in Settings › Subscription. ${PLAN_NAME} runs to the end of the period you paid for, then your household moves to Free. Nothing is deleted. Paid periods aren’t refunded, except where the law requires it. A new subscription can be withdrawn from within 14 days.` },
      { q: 'Can two of us shop at the same time?', a: 'Yes. Start shopping on one phone or watch, and everyone else in your household sees Join with your name. You share one trip and one basket, even if you split up in the store, and Finish trip ends it for everyone, each with the summary.' },
      { q: 'Do I need to install anything?', a: 'No. Matlist runs in the browser. On a phone, add it to your home screen and it works like an app.' },
      { q: 'Which Garmin watches are supported?', a: 'Most recent Garmin watches with a colour screen: Forerunner 70, 165, 170, 255, 265, 570, 955, 965 and 970, fēnix 7 and later, epix (Gen 2) and epix Pro, Enduro 2 and later, Venu 2 and later, vívoactive 5 and 6, the AMOLED Instincts, MARQ (Gen 2), quatix and tactix 7 and 8, D2, Descent Mk3 and G2, and Approach S50, S70 and S72. The Connect IQ Store listing names every model.' },
      { q: 'Can I import recipes from TikTok, Instagram and YouTube?', a: 'Yes. Paste the link, or share it to Matlist on Android. Matlist reads the recipe from the description or the caption, or from the recipe page it links to. If a post is private, paste its caption.' },
      { q: 'Does it work without signal?', a: 'Your list opens without signal. Adding and ticking things off need a connection, and everyone’s list catches up the moment you have one.' },
      { q: 'Which languages does it speak?', a: 'The app is in English for now. Your list can be in any language: Matlist matches English and Swedish names, so “mjölk” and “milk” are the same thing.' },
      { q: 'What happens to our data?', a: 'It’s stored in the EU and shared only with your household. No ads, no tracking, and we don’t sell it. You can download it in Settings and ask for it to be deleted at any time.' },
    ],
  },
  cta: { title: ['Get the list out', 'of the group chat.'] },
  form: { eyebrow: 'Get started', title: 'Create your list', region: 'Sign in or create an account' },
  waitlist: {
    cta: 'Join the waitlist',
    eyebrow: 'Coming soon',
    title: 'Be first on the list',
    body: OFFER
      ? `Matlist opens soon. Leave your email and you’ll hear the day it does. The founding offer, ${OFFER.percentOff}% off the first year of ${PLAN_NAME}, is for the first ${OFFER.households} households.`
      : 'Matlist opens soon. Leave your email and you’ll hear the day it does.',
    region: 'Join the waitlist',
    cost: 'Matlist hasn’t opened yet: join the waitlist and you’ll hear the day it does.',
    notMember: 'Not a member yet? Join the waitlist',
    member: 'Already a member?',
  },
  footer: { privacy: 'Privacy', terms: 'Terms' },
}

const sv: LandingCopy = {
  meta: {
    title: 'Matlist: delad inköpslista för hushållet och din Garmin',
    description: 'En inköpslista för hela hushållet. Spara recept från vilken sajt som helst, YouTube, TikTok eller Instagram, planera veckan och handla i butikens ordning i mobilen eller på din Garmin-klocka.',
    ogTitle: 'Matlist: planera hemma, handla från handleden',
    ogDescription: 'En inköpslista för hela hushållet, i varje mobil och på din Garmin-klocka.',
    ogImageAlt: 'Matlist: planera hemma, handla från handleden. Den delade listan i en mobil och på en Garmin-klocka.',
    locale: 'sv_SE',
  },
  nav: { how: 'Så funkar det', recipes: 'Recept', garmin: 'Garmin', pricing: 'Pris', faq: 'Frågor', signIn: 'Logga in', open: 'Öppna Matlist', otherLang: 'English', sections: 'Avsnitt' },
  hero: {
    eyebrow: 'Delad inköpslista',
    title: ['Planera hemma.', 'Handla från', 'handleden.'],
    lead: 'Matlist är en inköpslista för hela hushållet. Planera veckan utifrån era recept och handla sedan i butikens ordning, i mobilen eller på din Garmin-klocka.',
    garminCta: 'Skaffa Garmin-appen',
    worksOn: 'Fungerar på',
    platforms: ['Webben', 'iPhone', 'Android', 'iPad och dator', 'Garmin-klockor'],
  },
  film: {
    eyebrow: 'På 47 sekunder',
    title: ['Handled. Butik.', 'Tillsammans. Recept.'],
    alt: 'En kort film i fyra delar. Varor som bockas av på en Garmin-klocka medan mobilen ligger kvar i fickan; Matlist fungerar på fler än 70 Garmin-klockor. En lista som sorterar sig i den ordning du går genom butiken och lär sig av varje handling. Att gå med någon som redan handlar, så att det du bockar av på mobilen eller klockan försvinner på båda. Ett recept från en länk på TikTok, Instagram, YouTube eller webben som hamnar på listan, och som först frågar om äggen som redan står där. Mobilens och klockans skärmar är den riktiga appen.',
  },
  shop: {
    eyebrow: 'Tillsammans',
    title: ['Bocka av på en.', 'Borta på båda.'],
    body: 'Alla lägger till på samma lista. I butiken är ni en handling med en korg: det en av er bockar av försvinner från alla mobiler och klockor inom en sekund, så ingen köper mjölken två gånger.',
    points: [
      { lead: 'Gå med', text: 'när någon redan handlar: namnet står på knappen, i mobilen och på klockan.' },
      { lead: 'En korg:', text: 'svep åt höger i mobilen eller tryck Start på klockan, så ligger den där. Ångra är ett tryck bort.' },
      { lead: 'Avsluta handlingen', text: 'avslutar den för alla och visar var och en sammanfattningen.' },
    ],
  },
  store: {
    eyebrow: 'Din butik',
    title: ['Listan går', 'din väg genom', 'butiken.'],
    body: 'Välj butik när du börjar handla så lägger sig listan i den ordning du går. Varje runda lär den lite mer, så nästa går fortare.',
    points: [
      { lead: 'Börja handla', text: 'och välj butik. En ny butik? Då sorteras listan efter avdelning tills den lärt sig.' },
      { lead: 'Lär sig av varje runda:', text: 'den ordning du lägger saker i korgen är den ordning den visar nästa gång.' },
      { lead: 'Öppnas utan täckning', text: 'och kommer ikapp så fort du är uppkopplad igen.' },
    ],
    routeLabel: 'I din gångordning',
    route: [['Surdegsbröd', 'bakery'], ['Bananer', 'produce'], ['Äpplen', 'produce'], ['Pasta', 'pantry'], ['Ärtor', 'frozen'], ['Mjölk', 'dairy'], ['Ägg', 'dairy']],
  },
  plan: {
    eyebrow: 'Hemma',
    title: ['Planera hemma.'],
    body: 'På en dator eller iPad blir Matlist en planeringstavla. Varje kategori är öppen och har ett eget fält, så veckohandlingen är planerad på ett par minuter.',
    shotAlt: 'Planeringstavlan på en dator: alla kategorier öppna, var och en med ett eget fält.',
  },
  recipes: {
    eyebrow: 'Recept',
    title: ['Vilket recept', 'som helst.', 'Rakt till listan.'],
    body: 'Klistra in en länk från en receptsajt, YouTube, TikTok eller Instagram. Matlist läser receptet och kallar varje ingrediens det ni redan brukar kalla den, så inget hamnar på listan två gånger.',
    points: [
      { lead: 'Importera vilken länk som helst:', text: 'receptsajter, YouTube, TikTok och Instagram. Bilden och sidans betyg följer med.' },
      { lead: 'Lägg till på listan', text: 'slår ihop det som redan finns och frågar innan något blir dubbelt.' },
      { lead: 'Planera veckan', text: 'dag för dag, och laga mat med en timer för varje steg.' },
    ],
    links: ['tiktok.com/@…/video/…', 'instagram.com/reel/…', 'youtube.com/watch?v=…', 'vilken receptsida som helst'],
  },
  usual: {
    eyebrow: 'Snart slut',
    title: ['Den minns', 'det vanliga.'],
    body: 'Matlist märker hur ofta ni köper saker. När havremjölken brukar ta slut dyker den upp som ett förslag. Ett tryck och den är tillbaka på listan.',
    chipsLabel: 'Snart slut',
    chips: [['Havremjölk', 'Var 5:e dag', 'dairy'], ['Ägg', 'För 8 dagar sedan', 'dairy'], ['Bananer', 'Varje vecka', 'produce'], ['Kaffebönor', 'Varannan vecka', 'pantry']],
  },
  garmin: {
    eyebrow: 'Garmin',
    title: ['Bocka av.', 'Mobilen stannar', 'i fickan.'],
    body: 'Börja handla på klockan så rullar listan längs skärmens rundning i butikens ordning. Tryck Start så ligger varan i korgen, med Ångra precis vid Bakåt-knappen.',
    points: [
      'Gå med i någons handling direkt från handleden',
      'Allt går med knapparna, ingen pekskärm behövs',
      'Paras på några sekunder: skanna klockans kod och skriv in koden från appen',
    ],
    stat: { value: '70+', label: 'Garmin-klockor: Forerunner, fēnix, Venu med flera' },
  },
  more: {
    eyebrow: 'Och mer',
    title: ['Allt runt', 'handlingen.'],
    summary: {
      title: 'Handlingen i siffror',
      body: 'Avsluta en handling så får alla sammanfattningen: tiden i butiken, avdelning för avdelning, vem som lade till vad och vad det kostade.',
      stats: [['14:32', 'I butiken'], ['12', 'Varor'], ['5', 'Avdelningar']],
    },
  },
  setup: {
    title: ['Igång på en minut.'],
    steps: [
      { title: 'Skapa ett hushåll', text: 'Öppna Matlist, namnge ert hushåll så får du en kort kod.' },
      { title: 'Dela koden', text: 'Alla skriver in den i sin mobil. Klockan paras med en kod från Inställningar.' },
      { title: 'Handla tillsammans', text: 'Lägg till var du än är. Den som står i butiken bockar av.' },
    ],
  },
  compare: {
    eyebrow: 'Jämförelse',
    title: ['Så står sig', 'Matlist.'],
    body: 'De flesta listappar delar en lista. Matlist är byggd för hela handlingen: planeringen, butiken och handleden.',
    columns: ['Matlist', 'Bring!', 'AnyList', 'Google Keep'],
    rows: [
      { label: 'En lista som hushållet redigerar live', cells: [true, true, true, true] },
      { label: 'Garmin-klockapp', cells: [true, false, false, false] },
      { label: 'Andra klockor', cells: [false, 'Apple Watch, Wear OS', 'Apple Watch', 'Wear OS'] },
      { label: 'Recept från webblänkar', cells: [true, true, true, false] },
      { label: 'Recept från YouTube, TikTok och Instagram', cells: [true, '', '', false] },
      { label: 'Butikens ordning, inlärd från era rundor', cells: [true, '', '', false] },
      { label: 'Körs i webbläsaren, inget att installera', cells: [true, '', '', true] },
      { label: 'Gratisplan med delad lista', cells: [`Ja, upp till ${FREE_ITEM_LIMIT} varor`, true, true, true] },
      { label: 'Betald plan per år (pris i USA)', cells: ['$14.99, hela hushållet', '$8.99 (Premium)', '$14.99 hushåll, $9.99 en person', 'Ingen betald plan'] },
    ],
    note: `Övriga appar enligt deras egna beskrivningar i appbutiker, prissidor och hjälpsidor, ${comparedOn('sv')}. Priserna är de som varje app anger i USA, före moms. En tom ruta betyder att vi inte kunde avgöra säkert.`,
    yes: 'Ja',
    no: 'Nej',
  },
  pricing: {
    eyebrow: 'Pris', title: `${TRIAL_DAYS} dagar med allt. Sedan Free, eller ${PLAN_NAME} för hela hushållet.`,
    howTitle: 'Så fungerar det',
    how: [
      `Börja med ${TRIAL_DAYS} dagar med allt, klockappen också. Inget kort, inget att säga upp.`,
      `När provperioden tar slut går ert hushåll över till Free av sig självt: den delade listan fortsätter fungera med upp till ${FREE_ITEM_LIMIT} varor åt gången. Inget raderas och inget dras.`,
      `Skaffa ${PLAN_NAME} när ni vill, per år eller per månad, för alla i hushållet. Avsluta när du vill: ${PLAN_NAME} gäller till slutet av perioden ni betalat för, sedan är ni på Free igen.`,
    ],
    free: {
      name: 'Free', price: 'Gratis', per: 'så länge ni vill',
      features: [
        `En delad lista, synkad live, upp till ${FREE_ITEM_LIMIT} varor åt gången`,
        'Handlingsläge, med listan i gångordning',
        'Era sparade recept, att läsa och laga mat från',
        `Upp till ${MAX_MEMBERS} personer`,
      ],
    },
    plus: {
      name: PLAN_NAME, perYear: 'per år',
      orMonthly: p => `eller ${p} i månaden`, bestValue: p => `Mest prisvärt: under ${p} i månaden`,
      features: [
        'Allt i Free',
        'Obegränsat antal varor',
        'Garmin-klockappen: bocka av från handleden',
        'Receptimport från vilken länk som helst, TikTok, Instagram och YouTube',
        'Veckomeny och veckomallar',
        'Er butik inlärd i gångordning',
        'Snart slut och Tidigare köpt',
        'Statistik över era rundor och utgifter',
      ],
    },
    cta: `Prova gratis i ${TRIAL_DAYS} dagar`,
    small: `Ni betalar per hushåll, inte per person: ett ${PLAN_NAME} räcker för upp till ${MAX_MEMBERS} personer.`,
    founder: OFFER && {
      title: `Grundarerbjudande: ${OFFER.percentOff} % rabatt första året`,
      points: (first, then) => [
        `${PLAN_NAME} per år för ${first} första året, sedan ${then} per år.`,
        `För de första ${OFFER.households} hushållen, i ${OFFER.days} dagar efter lanseringen.`,
        'Dras automatiskt när ni väljer per år. Ingen kod att skriva.',
        'För nya kunder, ett per hushåll. Månadsplanen har ingen rabatt.',
        'Den förnyas till fullt pris. Avsluta när du vill innan dess.',
      ],
    },
    tax: included => (included ? 'Moms ingår. Avsluta när du vill.' : 'Plus moms där det gäller. Avsluta när du vill.'),
    conversion: 'I kassan kan beloppet visas i din egen valuta.', pricesIn: 'Priser i', change: 'Ändra', currency: 'Valuta',
  },
  faq: {
    title: 'Frågor',
    items: [
      { q: 'Vad kostar det?', a: `Matlist börjar med ${TRIAL_DAYS} dagar med allt, gratis och utan kort. Sedan är ert hushåll på Free, den delade listan med upp till ${FREE_ITEM_LIMIT} varor åt gången, så länge ni vill. ${PLAN_NAME} är allt, för upp till ${MAX_MEMBERS} personer: ${inEveryCurrency('annual', 'sv')} per år, eller ${inEveryCurrency('monthly', 'sv')} i månaden, plus moms där det gäller.` },
      { q: 'Vad är gratis?', a: `Den delade listan, live i allas mobiler, med upp till ${FREE_ITEM_LIMIT} varor åt gången; en avbockad vara frigör sin plats. Kategorier, handlingsläge med korgen, sammanfattningen av rundan, era sparade recept att läsa och laga mat från, och upp till ${MAX_MEMBERS} personer. Klockan visar listan; att bocka av från handleden ingår i ${PLAN_NAME}.` },
      { q: 'Vad händer när provperioden tar slut?', a: `Inget ni behöver göra. Ert hushåll går över till Free av sig självt, behåller all sin data och inget dras. Har listan fler än ${FREE_ITEM_LIMIT} varor ligger allt kvar; ni kan lägga till igen när den är under ${FREE_ITEM_LIMIT}. Skaffa ${PLAN_NAME} när som helst så får ni tillbaka allt.` },
      ...(OFFER ? [{ q: 'Hur fungerar grundarerbjudandet?', a: `De första ${OFFER.households} hushållen som väljer ${PLAN_NAME} per år inom ${OFFER.days} dagar efter lanseringen betalar ${OFFER.percentOff} % mindre första året. Rabatten dras automatiskt i kassan (det är koden ${OFFER.code}), så det finns inget att skriva. Den gäller nya kunder, en per hushåll, och inte månadsplanen. Efter första året förnyas ${PLAN_NAME} till fullt pris, och ni kan avsluta när ni vill.` }] : []),
      { q: 'Kan jag avsluta?', a: `Ja, när som helst, under Inställningar › Prenumeration. ${PLAN_NAME} gäller till slutet av perioden ni betalat för, sedan går hushållet över till Free. Inget raderas. Betalda perioder återbetalas inte, utom där lagen kräver det. En ny prenumeration kan ångras inom 14 dagar.` },
      { q: 'Kan vi handla två samtidigt?', a: 'Ja. Börja handla i en mobil eller på en klocka, så ser alla andra i hushållet Gå med och ditt namn. Ni delar en handling och en korg, även om ni delar upp er i butiken, och Avsluta handlingen avslutar den för alla, med sammanfattningen till var och en.' },
      { q: 'Behöver jag installera något?', a: 'Nej. Matlist körs i webbläsaren. Lägg till den på hemskärmen i mobilen så fungerar den som en app.' },
      { q: 'Vilka Garmin-klockor stöds?', a: 'De flesta nyare Garmin-klockor med färgskärm: Forerunner 70, 165, 170, 255, 265, 570, 955, 965 och 970, fēnix 7 och senare, epix (Gen 2) och epix Pro, Enduro 2 och senare, Venu 2 och senare, vívoactive 5 och 6, Instinct med AMOLED, MARQ (Gen 2), quatix och tactix 7 och 8, D2, Descent Mk3 och G2 samt Approach S50, S70 och S72. I Connect IQ Store ser du alla modeller.' },
      { q: 'Kan jag importera recept från TikTok, Instagram och YouTube?', a: 'Ja. Klistra in länken, eller dela den till Matlist på Android. Matlist läser receptet ur beskrivningen eller bildtexten, eller från receptsidan den länkar till. Är inlägget privat klistrar du in bildtexten.' },
      { q: 'Fungerar det utan täckning?', a: 'Listan öppnas utan täckning. För att lägga till och bocka av behövs uppkoppling, och allas listor kommer ikapp så fort den finns.' },
      { q: 'Vilka språk finns?', a: 'Appen är på engelska än så länge. Listan kan vara på vilket språk som helst: Matlist matchar svenska och engelska namn, så ”mjölk” och ”milk” är samma sak.' },
      { q: 'Vad händer med vår data?', a: 'Den lagras inom EU och delas bara med ert hushåll. Inga annonser, ingen spårning, och vi säljer den inte. Du kan ladda ner den under Inställningar och när som helst be om att få den raderad.' },
    ],
  },
  cta: { title: ['Få ut listan', 'ur gruppchatten.'] },
  form: { eyebrow: 'Kom igång', title: 'Skapa er lista', region: 'Logga in eller skapa ett konto' },
  waitlist: {
    cta: 'Skriv upp dig',
    eyebrow: 'Snart här',
    title: 'Bli först på listan',
    body: OFFER
      ? `Matlist öppnar snart. Lämna din e-post så hör du av oss den dagen. Grundarerbjudandet, ${OFFER.percentOff} % rabatt på första året med ${PLAN_NAME}, gäller de första ${OFFER.households} hushållen.`
      : 'Matlist öppnar snart. Lämna din e-post så hör du av oss den dagen.',
    region: 'Väntelistan',
    cost: 'Matlist har inte öppnat än: skriv upp dig på väntelistan så hör du av oss den dagen.',
    notMember: 'Inte medlem än? Skriv upp dig på väntelistan',
    member: 'Redan medlem?',
  },
  footer: { privacy: 'Integritet', terms: 'Villkor' },
}

export const LANDING_COPY: Record<LandingLang, LandingCopy> = { en, sv }

/** Whether the landing page sells (open) or collects the waitlist (before launch, VITE_COMING_SOON). */
export type LandingMode = 'open' | 'waitlist'

/**
 * The FAQ as the page shows it. Before launch every answer is the same — the
 * pricing is as open on the waitlist as after launch — and the price question
 * adds that Matlist hasn't opened yet.
 */
export function faqFor(lang: LandingLang, mode: LandingMode): LandingCopy['faq']['items'] {
  const t = LANDING_COPY[lang]
  if (mode === 'open') return t.faq.items
  return t.faq.items.map((f, i) => (i === 0 ? { ...f, a: `${f.a} ${t.waitlist.cost}` } : f))
}

/** The landing language a path asks for: /sv and below is Swedish, everything else English. */
export function landingLangOf(pathname: string): LandingLang {
  return /^\/sv(\/|$)/i.test(pathname) ? 'sv' : 'en'
}
